import React, { useState } from 'react';
import PageHeader from '../common/PageHeader';

const SoulWinning = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    area: '',
    experience: '',
    availability: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch('/api/contact/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          subject: 'Soul Winning Ministry Application',
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          area: '',
          experience: '',
          availability: '',
          message: '',
        });
      } else {
        setSubmitError(data.message || 'There was an error submitting your application. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting soul winning application:', error);
      setSubmitError('There was an error submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="container mx-auto px-4 py-8">
      <PageHeader 
        title="Soul Winning Ministry" 
        subtitle="Join us in spreading the gospel to our community and beyond"
        imageUrl="https://images.unsplash.com/photo-1508888965200-3af1b13dbf3b?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
        imageAlt="Soul Winning Ministry"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
        {/* Information Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">About Soul Winning</h2>
          <p className="text-gray-700 mb-4">
            Soul winning is the act of sharing the gospel of Jesus Christ with those who have not yet accepted Him as their Savior. 
            It is a vital ministry in our church, as we believe in the Great Commission to "go and make disciples of all nations."
          </p>
          <p className="text-gray-700 mb-4">
            Our Soul Winning Ministry is dedicated to equipping and sending believers to share the love of Christ with others. 
            We provide training, resources, and opportunities to engage in evangelism both locally and globally.
          </p>
          
          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">What We Do</h3>
          <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
            <li>Street evangelism in our local community</li>
            <li>Door-to-door outreach programs</li>
            <li>Evangelism training workshops</li>
            <li>Mission trips to unreached areas</li>
            <li>Follow-up and discipleship of new believers</li>
          </ul>
          
          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Get Involved</h3>
          <p className="text-gray-700 mb-4">
            We are always looking for passionate individuals who want to share the gospel with others. 
            Whether you are a seasoned evangelist or new to sharing your faith, there is a place for you in our Soul Winning Ministry.
          </p>
          <p className="text-gray-700 mb-4">
            Fill out the application form to join our team, and we will contact you with more information about upcoming training sessions and outreach opportunities.
          </p>
          
          <div className="mt-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Upcoming Events</h3>
            <div className="space-y-4">
              <div className="border-l-4 border-indigo-500 pl-4">
                <h4 className="font-semibold text-gray-900">Soul Winning Training</h4>
                <p className="text-gray-600">Every Saturday, 10:00 AM - 12:00 PM</p>
                <p className="text-gray-600">Main Campus, Room 101</p>
              </div>
              <div className="border-l-4 border-indigo-500 pl-4">
                <h4 className="font-semibold text-gray-900">Community Outreach</h4>
                <p className="text-gray-600">First Saturday of each month, 9:00 AM - 2:00 PM</p>
                <p className="text-gray-600">Meet at the church parking lot</p>
              </div>
              <div className="border-l-4 border-indigo-500 pl-4">
                <h4 className="font-semibold text-gray-900">Mission Trip Planning Meeting</h4>
                <p className="text-gray-600">Third Tuesday of each month, 6:30 PM - 8:00 PM</p>
                <p className="text-gray-600">Fellowship Hall</p>
              </div>
            </div>
          </div>
        </div>

        {/* Application Form */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Join Our Soul Winning Team</h2>
          
          {submitSuccess ? (
            <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-md mb-6">
              <p className="font-medium">Thank you for your interest in our Soul Winning Ministry!</p>
              <p className="mt-2">We have received your application and will contact you soon with more information.</p>
              <button 
                onClick={() => setSubmitSuccess(false)}
                className="mt-4 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition duration-200"
              >
                Submit Another Application
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {submitError && (
                <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md">
                  <p>{submitError}</p>
                </div>
              )}
              
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              
              <div>
                <label htmlFor="area" className="block text-sm font-medium text-gray-700 mb-1">
                  Preferred Ministry Area
                </label>
                <select
                  id="area"
                  name="area"
                  value={formData.area}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select an area</option>
                  <option value="street">Street Evangelism</option>
                  <option value="door">Door-to-Door Outreach</option>
                  <option value="training">Evangelism Training</option>
                  <option value="missions">Mission Trips</option>
                  <option value="follow-up">Follow-up & Discipleship</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-1">
                  Previous Evangelism Experience
                </label>
                <textarea
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Tell us about any previous experience you have with evangelism or sharing your faith."
                ></textarea>
              </div>
              
              <div>
                <label htmlFor="availability" className="block text-sm font-medium text-gray-700 mb-1">
                  Availability
                </label>
                <textarea
                  id="availability"
                  name="availability"
                  value={formData.availability}
                  onChange={handleChange}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="What days and times are you available to serve?"
                ></textarea>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Information
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Is there anything else you would like us to know?"
                ></textarea>
              </div>
              
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition duration-200 disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
              
              <p className="text-xs text-gray-500 mt-2">
                * Required fields
              </p>
            </form>
          )}
        </div>
      </div>
      
      {/* Resources Section */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Soul Winning Resources</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Training Materials</h3>
            <ul className="space-y-2 text-gray-700">
              <li>
                <a href="#" className="text-indigo-600 hover:text-indigo-800">The Four Spiritual Laws</a>
              </li>
              <li>
                <a href="#" className="text-indigo-600 hover:text-indigo-800">Share Jesus Without Fear</a>
              </li>
              <li>
                <a href="#" className="text-indigo-600 hover:text-indigo-800">Evangelism Explosion</a>
              </li>
              <li>
                <a href="#" className="text-indigo-600 hover:text-indigo-800">Personal Testimony Guide</a>
              </li>
            </ul>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Online Courses</h3>
            <ul className="space-y-2 text-gray-700">
              <li>
                <a href="#" className="text-indigo-600 hover:text-indigo-800">Evangelism 101</a>
              </li>
              <li>
                <a href="#" className="text-indigo-600 hover:text-indigo-800">Apologetics for Evangelism</a>
              </li>
              <li>
                <a href="#" className="text-indigo-600 hover:text-indigo-800">Cross-Cultural Evangelism</a>
              </li>
              <li>
                <a href="#" className="text-indigo-600 hover:text-indigo-800">Digital Evangelism</a>
              </li>
            </ul>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Recommended Reading</h3>
            <ul className="space-y-2 text-gray-700">
              <li>
                <a href="#" className="text-indigo-600 hover:text-indigo-800">The Master Plan of Evangelism</a>
              </li>
              <li>
                <a href="#" className="text-indigo-600 hover:text-indigo-800">Out of the Saltshaker</a>
              </li>
              <li>
                <a href="#" className="text-indigo-600 hover:text-indigo-800">The Soul Winner</a>
              </li>
              <li>
                <a href="#" className="text-indigo-600 hover:text-indigo-800">Evangelism: How to Share the Gospel</a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SoulWinning; 